import { type FC, useMemo } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "../../common/form/useForm";
import { ExtraForm } from "../../common/pure/ExtraFormGroup";
import { useCompany, useCompanyActions } from "../../company/useCompany";
import { Button } from "../../components/Button";
import { MultiInput } from "../../components/MultiInput";
import type { StateSetter } from "../../helpers/types";
import { useOnboardContext } from "./Onboard";

type Props = {
	hasWebsite: boolean;
	setHasWebsite: StateSetter<boolean>;
	onSuccess?: () => void;
};

export const FormOverview: FC<Readonly<Props>> = ({
	hasWebsite,
	setHasWebsite,
	onSuccess,
}) => {
	const { prevSubstep } = useOnboardContext();
	const company = useCompany();
	const { updateCompany } = useCompanyActions();

	const form = useForm({
		initialValues: {
			name: company.name,
			description: company.description,
			tone: company.tone,
			voice: company.voice,
			website: hasWebsite ? company.website : undefined,
			locations: company.locations,
		},
		onSubmit: async (values) => {
			await updateCompany.mutateAsync(values);

			onSuccess?.();
		},
	});

	const locationsErrorsKeys = Object.keys(form.errors).filter(
		(key) => key.split(".")[0] === "locations",
	) as Array<keyof typeof form.errors>;

	const transformedErrorLocations = useMemo(
		() =>
			locationsErrorsKeys.map((idxKey) => {
				const [_, indexString] = idxKey.split(".");
				const index = Number(indexString);
				const error = form.errors[idxKey];
				return {
					index,
					error: Array.isArray(error) ? error[0] : error,
				};
			}),
		[locationsErrorsKeys, form],
	);

	const locationsErrors = useMemo(
		() =>
			form.values.locations.map((_, index) => {
				const e = transformedErrorLocations.find(
					(error) => error.index === index,
				);

				const hasError = e !== undefined;

				return hasError ? (e.error ?? null) : null;
			}),
		[transformedErrorLocations, form.values.locations],
	);

	return (
		<Form className="form-overview" onSubmit={form.handleSubmit}>
			<ExtraForm.Group label="Business Name">
				<ExtraForm.Control
					placeholder="Write the name of you business"
					{...form.getFieldProps("name")}
					{...form.getFieldState("name")}
				/>
			</ExtraForm.Group>

			{hasWebsite && (
				<ExtraForm.Group label="Website">
					<ExtraForm.Control
						{...form.getFieldProps("website")}
						{...form.getFieldState("website")}
					/>
				</ExtraForm.Group>
			)}

			<ExtraForm.Group label="Describe your business">
				<ExtraForm.Control
					as="textarea"
					rows={5}
					{...form.getFieldProps("description")}
					{...form.getFieldState("description")}
				/>
			</ExtraForm.Group>

			<ExtraForm.Group label="Marketing Tone">
				<ExtraForm.Control
					as="textarea"
					rows={5}
					{...form.getFieldProps("tone")}
					{...form.getFieldState("tone")}
				/>
			</ExtraForm.Group>

			<ExtraForm.Group label="Marketing Voice">
				<ExtraForm.Control
					as="textarea"
					rows={5}
					{...form.getFieldProps("voice")}
					{...form.getFieldState("voice")}
				/>
			</ExtraForm.Group>
			<ExtraForm.Group label="Locations">
				<MultiInput
					placeholder="Add a location"
					{...form.getFieldProps("locations")}
					{...form.getFieldState("locations")}
					error={locationsErrors}
					onChange={(locations) => form.setFieldValue("locations", locations)}
				/>
			</ExtraForm.Group>

			<div className="bar">
				<Button type="submit">Save Changes</Button>

				{setHasWebsite && !hasWebsite && (
					<Button
						variant="link"
						type="button"
						onClick={() => setHasWebsite(true)}
					>
						Back
					</Button>
				)}

				{hasWebsite && (
					<Button variant="link" type="button" onClick={() => prevSubstep()}>
						Back
					</Button>
				)}
			</div>
		</Form>
	);
};
